import React, { useState, useEffect, useRef } from 'react'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline'
import { write as XLSXWrite } from 'xlsx'
import TableComponent from 'components/common/TableComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import MonitorCard from 'components/Dashboard/MonitorCard'
import PageLoadingState from 'components/common/PageLoadingState'
import WorkerTimelineModal from 'components/modals/WorkerTimelineModal'
import DateRangeFilterModal from 'components/modals/DateRangeFilterModal'
import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'
import { useAuth } from 'auth/AuthContext'
import { buildAttendanceReportWorkbook } from 'utils/attendanceReportExcel'
import { useDashboardFilterOptions } from 'hooks/useDashboardFilterOptions'
import { buildDashboardDateRangeQuery, createDefaultDateRange, resolveDashboardDateRangeFilter } from 'utils/dateRangeFilters'

const getAttendanceSummary = async (farmId, dateRange) => {
  try {
    const response = await makeRequest({
      method: 'GET',
      url: `/dashboard/${farmId}/attendance-summary?${buildDashboardDateRangeQuery(dateRange)}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      }
    })
    return response.data
  } catch (error) {
    console.error('Error fetching attendance summary:', error)
    throw error
  }
}

const formatHours = (value) => `${(Number(value) || 0).toFixed(2)} h`

const AttendanceByGroupTable = ({ attendanceBoard }) => {
  const tableData = {
    hasCheckbox: false,
    columns: [
      { key: 'groupName', labelKey: 'columns.groupName' },
      { key: 'workersNumber', labelKey: 'columns.workers' },
      { key: 'startTime', labelKey: 'columns.firstLogin', cell: (value) => value || '—' },
      { key: 'endTime', labelKey: 'columns.lastActivity', cell: (value) => value || '—' },
      { key: 'totalTime', labelKey: 'columns.totalWorkHours', cell: (value) => formatHours(value) },
      {
        key: 'columnTasks',
        labelKey: 'columns.activities',
        cell: (value) => Array.isArray(value) && value.length > 0
          ? value.map(t => `${t.taskName} (${t.workerCount})`).join(', ')
          : '—'
      }
    ],
    rows: attendanceBoard
  }
  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />
}

const WorkersTimelineTable = ({ workersTimeline, onRowClick }) => {
  const tableData = {
    hasCheckbox: false,
    columns: [
      { key: 'workerId', labelKey: 'columns.workerId', cell: (value, row) => row.oldId || row.workerId },
      { key: 'workerName', labelKey: 'columns.workerName' },
      { key: 'groupName', labelKey: 'columns.groupName', cell: (value) => value || '—' },
      { key: 'entryTime', labelKey: 'columns.clockIn', cell: (value) => value || '—' },
      { key: 'officialEndTime', labelKey: 'columns.officialEnd', cell: (value) => value || '—' },
      { key: 'totalHours', labelKey: 'columns.totalWorkHours', cell: (value) => formatHours(value) },
      { key: 'segments', labelKey: 'columns.sectors', cell: (value) => (Array.isArray(value) ? value.length : 0) }
    ],
    rows: workersTimeline
  }
  return <TableComponent tableData={tableData} defaultSortColumn='workerName' onRowClick={onRowClick} />
}

const DailyAttendanceSummary = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const hasFetched = useRef(false)
  const currentDate = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd')
  const filterOptions = useDashboardFilterOptions(user?.farmId)
  const [filterDate, setFilterDate] = useState(currentDate)
  const [exportDateRange, setExportDateRange] = useState(() => createDefaultDateRange(currentDate))
  const [showExportModal, setShowExportModal] = useState(false)
  const [data, setData] = useState({ attendanceBoard: [], workersTimeline: [], loading: true, error: null })
  const [selectedWorker, setSelectedWorker] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [workerSearch, setWorkerSearch] = useState('')

  // Filter the Workers table by the visible Worker # (oldId or workerId), name,
  // or group, so searching by the worker number works like the Workdays screen.
  const filteredWorkersTimeline = data.workersTimeline.filter(row => {
    if (!workerSearch) return true
    const code = String(row.oldId ?? row.workerId ?? '').toLowerCase()
    const name = (row.workerName || '').toLowerCase()
    const group = (row.groupName || '').toLowerCase()
    return code.includes(workerSearch) || name.includes(workerSearch) || group.includes(workerSearch)
  })

  const fetchData = async (dateToFetch = filterDate) => {
    if (!user?.farmId) return
    try {
      setData(prev => ({ ...prev, loading: true, error: null }))
      const res = await getAttendanceSummary(user.farmId, createDefaultDateRange(dateToFetch))
      setData({
        attendanceBoard: res.attendanceBoard || [],
        workersTimeline: res.workersTimeline || [],
        loading: false,
        error: null
      })
    } catch (error) {
      setData(prev => ({ ...prev, loading: false, error: t('dashboard.attendance.loadError') }))
    }
  }

  useEffect(() => {
    if (!user?.farmId) return
    if (hasFetched.current) return
    hasFetched.current = true
    fetchData(filterDate)
  }, [user?.farmId])

  useEffect(() => {
    if (!hasFetched.current) return
    fetchData(filterDate)
  }, [filterDate])

  const openTimeline = (row) => {
    setSelectedWorker(row)
    setIsModalOpen(true)
  }

  const openExportModal = () => {
    setExportDateRange(createDefaultDateRange(filterDate))
    setShowExportModal(true)
  }

  const handleExportExcel = async (rangeToExport) => {
    if (!user?.farmId || exporting) return
    try {
      setExporting(true)
      // The monitor sheet comes from the agro-summary endpoint for the same day.
      const query = buildDashboardDateRangeQuery(rangeToExport)
      const [attendanceExport, agroResponse] = await Promise.all([
        getAttendanceSummary(user.farmId, rangeToExport),
        makeRequest({
          method: 'GET',
          url: `/dashboard/${user.farmId}/agro-summary?${query}`,
          headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
        })
      ])
      const workersMonitor = agroResponse.data?.workersMonitor || []
      const exportDate = resolveDashboardDateRangeFilter(rangeToExport, currentDate).dateTo || currentDate

      const workbook = buildAttendanceReportWorkbook({
        workersTimeline: attendanceExport.workersTimeline || [],
        workersMonitor,
        filterDate: exportDate
      })
      const excelBuffer = XLSXWrite(workbook, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `Reporte_${exportDate}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      showAlert({ text: t('dashboard.attendance.excelDownloaded'), icon: 'success' })
      setExportDateRange(rangeToExport)
      setShowExportModal(false)
    } catch (error) {
      console.error('Error exporting attendance report:', error)
      showAlert({ text: t('dashboard.attendance.exportFailed'), icon: 'error' })
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className='p-6 bg-gray-50 min-h-screen'>
      <div className='flex justify-between items-start mb-4'>
        <div>
          <h1 className='text-2xl font-semibold text-green-600'>{t('dashboard.attendance.title')}</h1>
          <p className='text-sm text-gray-600'>{t('dashboard.attendance.attendanceFor', { date: filterDate })}</p>
        </div>
        <div className='flex items-center gap-3'>
          <label className='flex items-center gap-2 text-sm text-gray-700'>
            <span>{t('dashboard.attendance.date')}</span>
            <input type='date' value={filterDate} max={currentDate} onChange={(event) => setFilterDate(event.target.value)} className='p-2 border rounded-md shadow-sm bg-white' />
          </label>
          <ButtonComponent
            className='flex items-center gap-2'
            disabled={data.loading || exporting}
            onClick={openExportModal}
          >
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>{exporting ? t('common.exporting') : t('common.exportExcel')}</span>
          </ButtonComponent>
        </div>
      </div>

      <hr className='border-t border-gray-300 mb-6' />

      {data.loading && <PageLoadingState message={t('dashboard.attendance.loading')} rows={12} />}

      {!data.loading && data.error && <div className='text-red-500'>{data.error}</div>}

      {!data.loading && !data.error && (
        <>
          <MonitorCard title={t('dashboard.attendance.attendanceByGroup')} date={filterDate} hasData={data.attendanceBoard.length > 0} color='green'>
            <AttendanceByGroupTable attendanceBoard={data.attendanceBoard} />
          </MonitorCard>

          <MonitorCard title={t('dashboard.attendance.workers')} date={filterDate} hasData={data.workersTimeline.length > 0} color='green'>
            <div className='flex items-center justify-between gap-3 mb-2'>
              <p className='text-xs text-gray-500'>{t('dashboard.attendance.clickWorker')}</p>
              <input
                type='text'
                value={workerSearch}
                onChange={(e) => setWorkerSearch(e.target.value.toLowerCase())}
                placeholder={t('dashboard.attendance.searchWorker')}
                className='p-2 border rounded-md shadow-sm bg-white text-sm w-64'
              />
            </div>
            <WorkersTimelineTable workersTimeline={filteredWorkersTimeline} onRowClick={openTimeline} />
          </MonitorCard>
        </>
      )}

      <WorkerTimelineModal
        isOpen={isModalOpen}
        worker={selectedWorker}
        capTime={filterDate === currentDate
          ? DateTime.now().setZone('America/Mexico_City').toFormat('HH:mm')
          : null}
        onClose={() => setIsModalOpen(false)}
      />
      <DateRangeFilterModal
        isOpen={showExportModal}
        dateRange={exportDateRange}
        sectors={filterOptions.sectors}
        farmingBlocks={filterOptions.farmingBlocks}
        groups={filterOptions.groups}
        activities={filterOptions.activities}
        maxDate={currentDate}
        title={t('dashboard.attendance.exportModalTitle')}
        applyLabel={t('common.download')}
        showClearFilters={false}
        isSubmitting={exporting}
        onClose={() => setShowExportModal(false)}
        onApply={handleExportExcel}
      />
    </div>
  )
}

export default DailyAttendanceSummary
